import type { KeyboardEvent } from 'react';
import { fold } from '../lib/fold';
import { Combobox } from './Combobox';
import { IconXMark } from './icons';

/**
 * Free tags: a text field that suggests tags already in use, with the tags
 * added so far as removable chips below it. Enter or a comma adds what was
 * typed, Backspace in the empty field takes the last tag back. A tag already
 * there, ignoring case and accents, is not added twice.
 *
 * The typed text is the form's too: a form saved before Enter still has it
 * and can keep it as a tag.
 */
export function TagInput({
  id,
  label,
  value,
  onChange,
  draft,
  onDraftChange,
  suggestions,
  removeLabel,
  maxLength,
}: {
  id: string;
  label: string;
  value: readonly string[];
  onChange: (tags: string[]) => void;
  /** The text in the field, not yet a tag. */
  draft: string;
  onDraftChange: (draft: string) => void;
  /** Tags used elsewhere; the ones already added are left out. */
  suggestions: readonly string[];
  /** The remove button's accessible name, given the tag. */
  removeLabel: (tag: string) => string;
  maxLength?: number;
}) {
  const taken = new Set(value.map(fold));
  const options = suggestions.filter((s) => !taken.has(fold(s)));

  function add(raw: string[]) {
    const next = [...value];
    for (const tag of raw.map((t) => t.trim())) {
      if (tag !== '' && !next.some((t) => fold(t) === fold(tag)))
        next.push(tag);
    }
    if (next.length !== value.length) onChange(next);
    onDraftChange('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      // Adds the tag instead of submitting the form.
      e.preventDefault();
      add([draft]);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div>
      <Combobox
        id={id}
        label={label}
        value={draft}
        onChange={(next) => {
          // A comma — typed or pasted — ends a tag.
          if (next.includes(',')) {
            const parts = next.split(',');
            add(parts.slice(0, -1));
            onDraftChange(parts.at(-1) ?? '');
          } else {
            onDraftChange(next);
          }
        }}
        options={options}
        optionKey={(tag) => tag}
        optionLabel={(tag) => tag}
        onPick={(tag) => add([tag])}
        maxLength={maxLength}
        onKeyDown={onKeyDown}
      />
      {value.length > 0 && (
        <ul className="tag-input-chips">
          {value.map((tag) => (
            <li key={tag} className="tag-input-chip">
              {tag}
              <button
                type="button"
                className="tag-input-remove"
                aria-label={removeLabel(tag)}
                onClick={() => onChange(value.filter((t) => t !== tag))}
              >
                <IconXMark />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
